"use client";

import { useToast } from "@/context/ToastContext";
import {
  clearUsageOverrideAction,
  createUsageLimitRequestAction,
  fetchUsageBlocksAction,
  fetchUsageLimitRequestsAction,
  fetchUsageOverridesAction,
  fetchUsageOverviewAction,
  liftUsageBlockAction,
  resolveUsageLimitRequestAction,
  setUsageBlockAction,
  setUsageOverrideAction,
  type CreateUsageLimitRequestParamsType,
  type ResolveUsageLimitRequestParamsType,
  type SetUsageBlockParamsType,
  type SetUsageOverrideParamsType,
} from "@/utils/serverActions/usage/usageLimitActions";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { handleHookError } from "utils/helpers/handleError";

export const usageQueryKeys = {
  requests: ["usageLimitRequests"] as const,
  overview: ["usageOverview"] as const,
  blocks: ["usageBlocks"] as const,
  overrides: ["usageOverrides"] as const,
};

/** Every company's limit requests (administrators of company 1). */
export const useUsageLimitRequests = (enabled = true) =>
  useQuery({
    queryKey: usageQueryKeys.requests,
    queryFn: () => fetchUsageLimitRequestsAction(),
    enabled,
  });

/** Limit and use of every company (administrators of company 1). */
export const useUsageOverview = (enabled = true) =>
  useQuery({
    queryKey: usageQueryKeys.overview,
    queryFn: () => fetchUsageOverviewAction(),
    enabled,
  });

/** The company asks for more import capacity. */
export const useCreateUsageLimitRequest = () => {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (params: CreateUsageLimitRequestParamsType) => createUsageLimitRequestAction(params),
    onError: (error: Error) => {
      handleHookError(showToast, error, "No se pudo enviar la solicitud. Inténtalo de nuevo.");
    },
  });
};

/** Orquesti approves or denies a request. */
export const useResolveUsageLimitRequest = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: ResolveUsageLimitRequestParamsType) => resolveUsageLimitRequestAction(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usageQueryKeys.requests });
      queryClient.invalidateQueries({ queryKey: usageQueryKeys.overview });
    },
    onError: (error: Error) => {
      handleHookError(showToast, error, "No se pudo resolver la solicitud.");
    },
  });
};

/** Blocks of the imports of every company (administrators of company 1). */
export const useUsageBlocks = (enabled = true) =>
  useQuery({ queryKey: usageQueryKeys.blocks, queryFn: () => fetchUsageBlocksAction(), enabled });

/** Limits set by hand for companies (administrators of company 1). */
export const useUsageOverrides = (enabled = true) =>
  useQuery({ queryKey: usageQueryKeys.overrides, queryFn: () => fetchUsageOverridesAction(), enabled });

/** Everything on the platform screen that a block or a limit changes. */
const useRefreshUsage = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: usageQueryKeys.overview });
    queryClient.invalidateQueries({ queryKey: usageQueryKeys.blocks });
    queryClient.invalidateQueries({ queryKey: usageQueryKeys.overrides });
  };
};

export const useSetUsageBlock = () => {
  const { showToast } = useToast();
  const refresh = useRefreshUsage();
  return useMutation({
    mutationFn: (params: SetUsageBlockParamsType) => setUsageBlockAction(params),
    onSuccess: refresh,
    onError: (error: Error) => handleHookError(showToast, error, "No se pudo bloquear."),
  });
};

export const useLiftUsageBlock = () => {
  const { showToast } = useToast();
  const refresh = useRefreshUsage();
  return useMutation({
    mutationFn: (blockId: number) => liftUsageBlockAction(blockId),
    onSuccess: refresh,
    onError: (error: Error) => handleHookError(showToast, error, "No se pudo levantar el bloqueo."),
  });
};

export const useSetUsageOverride = () => {
  const { showToast } = useToast();
  const refresh = useRefreshUsage();
  return useMutation({
    mutationFn: (params: SetUsageOverrideParamsType) => setUsageOverrideAction(params),
    onSuccess: refresh,
    onError: (error: Error) => handleHookError(showToast, error, "No se pudo fijar el límite."),
  });
};

export const useClearUsageOverride = () => {
  const { showToast } = useToast();
  const refresh = useRefreshUsage();
  return useMutation({
    mutationFn: (params: { companyId: number; limitType: string }) => clearUsageOverrideAction(params),
    onSuccess: refresh,
    onError: (error: Error) => handleHookError(showToast, error, "No se pudo quitar el límite."),
  });
};
